// briefApi.js — client for the Intelligence Brief serverless endpoints.
// Mirrors the shape of src/lib/llm.js so the same rules apply: Supabase JWT
// is required, native builds hit the Vercel URL absolutely, and anything the
// server returns is passed through with the minimum amount of client-side
// massaging.

import { apiUrl } from './platform.js';
import { getFreshAccessToken, forceRefreshAccessToken } from './supabase.js';
import { promptForBundle } from './briefBundles.js';
import { todayIsoKey } from './dateKeys.js';
import { ensureAIConsent } from './aiConsent.js';
import { handleApiResponse } from './subscription.js';
import { PaywallRequiredError } from './llm.js';
// apiUrl from platform.js handles the iOS/Electron/web split: absolute
// Vercel URL on native shells, relative path in the browser.

// Run `doFetch(token)` with a freshly-validated token. If the server
// still returns 401 — meaning our local refresh produced a token the
// server nevertheless rejects (revoked refresh_token, clock skew, etc.)
// — try one forced refresh and fire the request once more. Anything
// else falls through with the original response so the caller can map
// it to its own error text.
async function authedFetch(doFetch, guestMessage) {
  const token = await getFreshAccessToken();
  if (!token) throw new Error(guestMessage);

  let res = await doFetch(token);
  if (res.status !== 401) return res;

  const retryToken = await forceRefreshAccessToken();
  if (!retryToken) return res;
  return await doFetch(retryToken);
}

// ── generateBrief ────────────────────────────────────────────────
// Build the system prompt for the chosen bundle, fire off the web-search
// enabled Claude call, and return the structured brief object.
//
// previousBrief (optional): the caller's latest prior brief for this bundle.
// When present, we tighten the web_search window to
//   [ max(firstOfCurrentMonth, previousBrief.generatedAt), now ]
// and tell the model which stories are already covered so consecutive
// editions do not overlap on six of seven days' worth of sources.
export async function generateBrief({
  bundleId, customFocus = '', edition = 1, previousBrief = null,
}) {
  // Apple 5.1.1(i)/5.1.2(i): in-app consent required before sending any
  // text (bundle + customFocus) to Anthropic.
  await ensureAIConsent();

  const today = todayIsoKey();
  const sourceWindow = computeSourceWindow(previousBrief);
  const previousInsights = Array.isArray(previousBrief?.insights) ? previousBrief.insights : [];

  const system = promptForBundle({
    bundleId, customFocus, today, edition,
    sourceWindow, previousInsights,
  });
  const userMessage = `Produce today's intelligence brief as JSON only. Bundle: ${bundleId}. Edition: ${edition}. Date: ${today}. Use web_search to pull up to eight stories first published between ${sourceWindow.startIso} and ${sourceWindow.endIso}. Surfacing stories older than ${sourceWindow.startIso} is a hard fail. Returning fewer than eight is acceptable. No preamble, no markdown, no commentary outside the JSON object.`;

  const res = await authedFetch(
    (token) => fetch(apiUrl('/api/brief'), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ system, userMessage }),
    }),
    'Please sign in to use the Intelligence Brief.'
  );

  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    if (res.status === 402) {
      handleApiResponse(res, err, 'brief_limit');
      throw new PaywallRequiredError(err?.usage);
    }
    if (res.status === 429) throw new Error(err.error || 'Daily Intelligence Brief limit reached.');
    if (res.status === 401) throw new Error('Your session expired. Please sign in again.');
    throw new Error(err.error || `Intelligence Brief service error (${res.status})`);
  }
  const data = await res.json();
  return data?.brief || null;
}

// ── fetchAudio ───────────────────────────────────────────────────
// Accepts the narration text plus a voice id, returns a Blob of MP3
// audio. The caller is responsible for caching the blob.
export async function fetchAudio({ text, voiceId }) {
  // Narration text is sent to ElevenLabs via /api/tts. Same in-app
  // disclosure + consent rule as the Anthropic and Whisper paths.
  await ensureAIConsent();

  const res = await authedFetch(
    (token) => fetch(apiUrl('/api/tts'), {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        'authorization': `Bearer ${token}`,
      },
      body: JSON.stringify({ text, voiceId }),
    }),
    'Please sign in to use the Intelligence Brief.'
  );

  if (!res.ok) {
    // /api/tts returns JSON on error, MP3 on success. handleTtsResponse
    // checks for 402 and surfaces the paywall before we throw.
    if (res.status === 402) {
      // res.json() consumes the body — handleTtsResponse already did that.
      // We need to re-clone before reading; simpler to read once here.
      const err = await res.json().catch(() => ({}));
      handleApiResponse(res, err, 'tts_limit');
      throw new PaywallRequiredError(err?.usage);
    }
    const err = await res.json().catch(() => ({}));
    if (res.status === 429) throw new Error(err.error || 'Daily TTS limit reached.');
    if (res.status === 401) throw new Error('Your session expired. Please sign in again.');
    // The 503 + ELEVENLABS_KEY_MISSING path is what the /api/tts endpoint
    // returns when the ElevenLabs API key is not configured on the
    // deployment. Surface a human-readable message instead of the cryptic
    // "TTS service error (503)" so the user (and the operator reading
    // their support email) immediately understands what's wrong.
    if (res.status === 503 && err?.code === 'ELEVENLABS_KEY_MISSING') {
      throw new Error('Narration is temporarily unavailable while we finish configuring the audio service.');
    }
    throw new Error(err.error || `TTS service error (${res.status})`);
  }
  return await res.blob();
}

// Build a clean narration script from a brief. We strip section numbers
// and URLs, keep titles and bodies, and speak "Strategic relevance:" as a
// label so the listener hears the structure.
export function narrationFromBrief(brief) {
  if (!brief || !Array.isArray(brief.insights)) return '';
  const parts = [];
  parts.push(`${brief.title || 'The Briefing'}. Edition ${brief.edition || ''}. ${humanDate(brief.date)}.`);
  brief.insights.forEach((ins, i) => {
    const n = i + 1;
    const title = stripForSpeech(ins.title);
    const body = stripForSpeech(ins.body);
    const rel  = stripForSpeech(ins.strategicRelevance);
    parts.push(`Insight ${n}. ${title}. ${body} Strategic relevance: ${rel}`);
  });
  return parts.filter(Boolean).join('\n\n');
}

function humanDate(iso) {
  if (!iso) return '';
  try {
    // Parse parts explicitly as local time to avoid iOS WebKit UTC drift.
    const [y, m, d] = iso.split('-').map(Number);
    if (!y || !m || !d) return iso;
    const date = new Date(y, m - 1, d);
    return date.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
  } catch { return iso; }
}

function stripForSpeech(s) {
  if (typeof s !== 'string') return '';
  return s
    .replace(/https?:\/\/\S+/g, '')          // drop bare URLs
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [label](url) -> label
    .replace(/[*_`#>]+/g, '')                // markdown noise
    .replace(/—/g, ', ')                     // house rule, no em dashes ever
    .replace(/\s+/g, ' ')
    .trim();
}

// ── computeSourceWindow ──────────────────────────────────────────
// Figure out the [start, now] window that web_search should be restricted to.
//
// Rule (agreed with product):
//   1. Never go back before the first of the current calendar month.
//      ("sources shouldn't go back from before the month")
//   2. If a prior brief exists for this bundle AND it was generated inside
//      the current month, tighten the start to its generatedAt timestamp.
//      ("if a brief was reported recently, sources should be between when
//       the last one was generated and the new one")
//
// Result: consecutive daily editions will only surface stories that broke
// since yesterday's edition, instead of re-scanning the same 7-day window
// and producing heavily overlapping content.
//
// Edge cases:
//   - No prior brief, today is April 1 -> window = [April 1 00:00 UTC, now].
//     Likely narrow but correct; honors the "never before the month" rule.
//   - Prior brief from last month -> falls back to firstOfMonth. We do NOT
//     cross the month boundary even if the last brief was only 3 days ago.
//   - Missing/invalid previousBrief.generatedAt -> fall back to firstOfMonth.
export function computeSourceWindow(previousBrief) {
  const now = new Date();
  const monthStart = new Date(Date.UTC(
    now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0,
  ));
  const endIso = now.toISOString();

  let startIso = monthStart.toISOString();
  let reason = 'first of current calendar month (no recent prior edition)';

  const prevRaw = previousBrief?.generatedAt;
  if (prevRaw) {
    const prev = new Date(prevRaw);
    if (!isNaN(prev.getTime()) && prev > monthStart) {
      startIso = prev.toISOString();
      reason = 'timestamp of the most recent prior edition of this bundle';
    }
  }

  return { startIso, endIso, reason };
}
